import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Divider,
  Alert,
  Grid,
  Chip,
} from '@mui/material';
import { Download, Refresh, School, FilterAlt } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { examinationAPI, branchAPI } from '../../../services/api';

const CenterwiseStudentList = () => {
  const [loadingExams, setLoadingExams] = useState(false);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [downloadingPDF, setDownloadingPDF] = useState(false);

  const [examinations, setExaminations] = useState([]);
  const [branches, setBranches] = useState([]);

  const [selectedExam, setSelectedExam] = useState('');
  const [selectedCenter, setSelectedCenter] = useState(''); // '' = All Centers

  const [reportData, setReportData] = useState(null); // JSON preview data

  useEffect(() => {
    fetchExaminations();
    fetchBranches();
  }, []);

  // When exam changes, reset everything
  const handleExamChange = (e) => {
    setSelectedExam(e.target.value);
    setSelectedCenter('');
    setReportData(null);
  };

  const fetchExaminations = async () => {
    try {
      setLoadingExams(true);
      const response = await examinationAPI.getAll({ limit: 1000 });
      setExaminations(response.data.data || response.data.examinations || []);
    } catch {
      toast.error('Failed to load examinations');
    } finally {
      setLoadingExams(false);
    }
  };

  const fetchBranches = async () => {
    try {
      setLoadingBranches(true);
      const response = await branchAPI.getAll({ limit: 1000 });
      setBranches(response.data.data || response.data.branches || []);
    } catch {
      toast.error('Failed to load centers');
    } finally {
      setLoadingBranches(false);
    }
  };

  const fetchPreviewData = async () => {
    if (!selectedExam) {
      toast.warning('Please select an examination');
      return;
    }
    try {
      setLoadingData(true);
      setReportData(null);
      const response = await examinationAPI.getCenterwiseStudentList(selectedExam, selectedCenter || null);
      const data = response.data.data;

      setReportData(data);
    } catch {
      toast.error('Failed to load student data');
    } finally {
      setLoadingData(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!selectedExam) {
      toast.warning('Please select an examination');
      return;
    }
    try {
      setDownloadingPDF(true);
      const response = await examinationAPI.getCenterwisePDF(
        selectedExam,
        selectedCenter || null
      );

      // Trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Try to get filename from Content-Disposition header
      const cd = response.headers['content-disposition'];
      const match = cd && cd.match(/filename="?([^";\n]+)"?/);
      link.download = match ? match[1] : `grade-sheet.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('PDF downloaded successfully');
    } catch {
      toast.error('Failed to download PDF');
    } finally {
      setDownloadingPDF(false);
    }
  };

  // Centers to display in the preview
  const displayCenters = reportData ? reportData.centers : [];

  return (
    <Box>
      {/* Page title */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <School sx={{ fontSize: 32, color: 'text.primary' }} />
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Centerwise Student List
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Print grade sheets for examinations — select exam and/or center, then download PDF
          </Typography>
        </Box>
      </Box>

      {/* Filter Panel */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <FilterAlt fontSize="small" />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Filters
          </Typography>
        </Box>

        <Grid container spacing={2} alignItems="flex-end">
          {/* Examination selector */}
          <Grid item xs={12} sm={5}>
            <FormControl fullWidth>
              <InputLabel>Examination *</InputLabel>
              <Select
                value={selectedExam}
                onChange={handleExamChange}
                label="Examination *"
                disabled={loadingExams}
                MenuProps={{ PaperProps: { style: { maxHeight: 320 } } }}
              >
                <MenuItem value="">— Select Examination —</MenuItem>
                <MenuItem value="all">All Examinations</MenuItem>
                {examinations.map((exam) => (
                  <MenuItem key={exam._id} value={exam._id}>
                    {exam.name} ({exam.code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Center selector */}
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Center (optional)</InputLabel>
              <Select
                value={selectedCenter}
                onChange={(e) => {
                  setSelectedCenter(e.target.value);
                  setReportData(null);
                }}
                label="Center (optional)"
                disabled={loadingBranches || !selectedExam}
                MenuProps={{ PaperProps: { style: { maxHeight: 320 } } }}
              >
                <MenuItem value="">All Centers</MenuItem>
                {branches.map((b) => (
                  <MenuItem key={b._id} value={b._id}>
                    {b.name} ({b.code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Action buttons */}
          <Grid item xs={12} sm={3}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={loadingData ? <CircularProgress size={14} /> : <Refresh />}
                onClick={fetchPreviewData}
                disabled={!selectedExam || loadingData}
                fullWidth
              >
                Preview
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={downloadingPDF ? <CircularProgress size={14} color="inherit" /> : <Download />}
                onClick={handleDownloadPDF}
                disabled={!selectedExam || downloadingPDF}
                fullWidth
              >
                PDF
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Summary chips */}
      {reportData && !loadingData && (
        <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
          <Chip variant="outlined" label={`Examination: ${reportData.examination?.name || 'N/A'}`} />
          <Chip variant="outlined" label={`Total Centers: ${reportData.totalCenters}`} />
          <Chip variant="outlined" label={`Total Students: ${reportData.totalStudents}`} />
          {selectedCenter && (
            <Chip
              variant="outlined"
              color="primary"
              label={`Filtered: ${branches.find(b => b._id === selectedCenter)?.name || 'Selected Center'}`}
            />
          )}
        </Box>
      )}

      {/* Loading spinner */}
      {loadingData && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Empty state */}
      {!loadingData && !reportData && (
        <Paper
          variant="outlined"
          sx={{ p: 6, textAlign: 'center', borderStyle: 'dashed' }}
        >
          <School sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Select an examination and click Preview or PDF
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            The PDF will be generated on the server and downloaded automatically.
            It includes Sl.No, Roll No., Register No., Student Name, Grade (blank) and Remarks (blank).
          </Typography>
        </Paper>
      )}

      {/* No students found */}
      {!loadingData && reportData && displayCenters.length === 0 && (
        <Alert severity="info">
          No approved students found for the selected filters.
        </Alert>
      )}

      {/* Preview Tables */}
      {!loadingData && displayCenters.length > 0 &&
        displayCenters.map((center, cidx) => (
          <Paper variant="outlined" key={center.branchId} sx={{ mb: 3 }}>
            {/* Center header bar */}
            <Box
              sx={{
                px: 2,
                py: 1.2,
                bgcolor: 'grey.100',
                borderBottom: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Center: {center.branchName} &nbsp;
                <Typography component="span" variant="body2" color="text.secondary">
                  ({center.branchCode})
                </Typography>
              </Typography>
              <Chip size="small" label={`${center.students.length} Students`} variant="outlined" />
            </Box>

            <TableContainer>
              <Table size="small" sx={{ tableLayout: 'fixed' }}>
                <colgroup>
                  <col style={{ width: '5%' }} />
                  <col style={{ width: '13%' }} />
                  <col style={{ width: '14%' }} />
                  <col style={{ width: '33%' }} />
                  <col style={{ width: '12%' }} />
                  <col style={{ width: '23%' }} />
                </colgroup>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    {['Sl.No', 'Roll No.', 'Reg. No.', 'Student Name', 'Grade', 'Remarks'].map((h) => (
                      <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.8rem', py: 1 }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {center.students.map((student, idx) => (
                    <TableRow
                      key={student._id}
                      sx={{ '&:nth-of-type(even)': { bgcolor: 'grey.50' } }}
                    >
                      <TableCell sx={{ fontSize: '0.78rem', py: 0.8 }}>{idx + 1}</TableCell>
                      <TableCell sx={{ fontSize: '0.78rem', py: 0.8 }}>{student.rollNumber}</TableCell>
                      <TableCell sx={{ fontSize: '0.78rem', py: 0.8 }}>{student.formNumber}</TableCell>
                      <TableCell sx={{ fontSize: '0.78rem', py: 0.8, fontWeight: 500 }}>{student.name}</TableCell>
                      {/* Blank grade & remarks — shown as empty cells */}
                      <TableCell sx={{ py: 0.8 }} />
                      <TableCell sx={{ py: 0.8 }} />
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Signature row */}
            <Box
              sx={{
                px: 2,
                py: 1.2,
                borderTop: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Total: {center.students.length} students
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Examiner Signature: __________________________ &nbsp;&nbsp; Date: __________
              </Typography>
            </Box>

            {cidx < displayCenters.length - 1 && <Divider />}
          </Paper>
        ))}
    </Box>
  );
};

export default CenterwiseStudentList;
